if (document.body?.classList.contains('sc-framework-reveal-enabled')) {
  document.documentElement.classList.add('sc-reveal-ready');
}

class ScDisclosureDrawer extends HTMLElement {
  connectedCallback() {
    this.openButtons = this.querySelectorAll('[data-sc-drawer-open]');
    this.closeButtons = this.querySelectorAll('[data-sc-drawer-close]');
    this.panel = this.querySelector('[data-sc-drawer-panel]');
    this.openTrigger = null;
    this.autoOpenEnabled = this.dataset.autoOpen === 'true';
    this.autoOpenDelay = Number(this.dataset.autoOpenDelay || 60);
    this.autoOpenScroll = Number(this.dataset.autoOpenScroll || 75);
    this.frequencyDays = Number(this.dataset.frequencyDays || 7);
    this.storageKey = this.dataset.storageKey || `sc_relationship_drawer_${this.id || 'default'}`;
    this.autoOpenTimer = null;
    this.autoOpenScrollHandler = null;
    this.handleHostClick = (event) => {
      if (event.target === this) this.close();
    };
    this.handleKeydown = (event) => this.onKeydown(event);
    this.openHandlers = Array.from(this.openButtons, (button) => {
      const handler = () => this.open(button);
      button.addEventListener('click', handler);
      return { button, handler };
    });
    this.closeHandlers = Array.from(this.closeButtons, (button) => {
      const handler = () => this.close();
      button.addEventListener('click', handler);
      return { button, handler };
    });
    this.addEventListener('click', this.handleHostClick);
    document.addEventListener('keydown', this.handleKeydown);
    this.setOpenState(false);
    this.scheduleAutoOpen();
  }

  disconnectedCallback() {
    this.openHandlers?.forEach(({ button, handler }) => button.removeEventListener('click', handler));
    this.closeHandlers?.forEach(({ button, handler }) => button.removeEventListener('click', handler));
    this.removeEventListener('click', this.handleHostClick);
    document.removeEventListener('keydown', this.handleKeydown);
    this.clearAutoOpen();
  }

  open(trigger) {
    this.clearAutoOpen();
    this.openTrigger = trigger || document.activeElement;
    this.setAttribute('open', '');
    this.setOpenState(true);
    const firstControl = this.panel?.querySelector('[data-sc-drawer-close], a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
    (firstControl || this.panel)?.focus();
  }

  close() {
    const wasOpen = this.hasAttribute('open');
    this.removeAttribute('open');
    this.setOpenState(false);
    if (wasOpen) this.rememberDismissal();
    if (this.openTrigger instanceof HTMLElement) {
      this.openTrigger.focus();
    }
  }

  readStorage() {
    try {
      return window.localStorage?.getItem(this.storageKey) || null;
    } catch (error) {
      return null;
    }
  }

  rememberDismissal() {
    try {
      window.localStorage?.setItem(this.storageKey, String(Date.now()));
    } catch (error) {
      // The drawer still works when storage is restricted; only frequency capping is skipped.
    }
  }

  wasRecentlyDismissed() {
    const stored = Number(this.readStorage());
    if (!stored || !Number.isFinite(stored)) return false;
    return Date.now() - stored < this.frequencyDays * 24 * 60 * 60 * 1000;
  }

  scheduleAutoOpen() {
    if (!this.autoOpenEnabled || this.wasRecentlyDismissed()) return;

    this.autoOpenTimer = window.setTimeout(() => {
      if (!this.hasAttribute('open') && !this.wasRecentlyDismissed()) this.open();
    }, Math.max(0, this.autoOpenDelay) * 1000);

    this.autoOpenScrollHandler = () => {
      if (this.hasAttribute('open') || this.wasRecentlyDismissed()) return;
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollProgress = scrollableHeight > 0 ? (window.scrollY / scrollableHeight) * 100 : 0;
      if (scrollProgress >= this.autoOpenScroll) this.open();
    };
    window.addEventListener('scroll', this.autoOpenScrollHandler, { passive: true });
  }

  clearAutoOpen() {
    if (this.autoOpenTimer) window.clearTimeout(this.autoOpenTimer);
    if (this.autoOpenScrollHandler) window.removeEventListener('scroll', this.autoOpenScrollHandler);
    this.autoOpenTimer = null;
    this.autoOpenScrollHandler = null;
  }

  setOpenState(isOpen) {
    this.openButtons?.forEach((button) => button.setAttribute('aria-expanded', String(isOpen)));
    if (this.panel) {
      this.panel.setAttribute('aria-hidden', String(!isOpen));
      this.panel.inert = !isOpen;
    }
    document.documentElement.classList.toggle('sc-modal-open', isOpen);
  }

  onKeydown(event) {
    if (!this.hasAttribute('open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab' || !this.panel) return;

    const focusable = Array.from(this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter((element) => !element.hidden && element.getClientRects().length);
    if (!focusable.length) {
      event.preventDefault();
      this.panel.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('sc-disclosure-drawer')) {
  customElements.define('sc-disclosure-drawer', ScDisclosureDrawer);
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-sc-open-relationship-drawer]');
  if (!trigger) return;

  const drawer = document.querySelector('sc-disclosure-drawer');
  if (!drawer || typeof drawer.open !== 'function') return;

  event.preventDefault();
  const headerDrawer = trigger.closest('header-drawer');
  if (headerDrawer && typeof headerDrawer.closeMenuDrawer === 'function') {
    headerDrawer.closeMenuDrawer(event, trigger);
  }
  drawer.open(trigger);
});

class ScAgeGate extends HTMLElement {
  connectedCallback() {
    this.storageKey = this.dataset.storageKey || 'sc_age_verified';
    this.verificationMethod = this.dataset.verificationMethod || 'confirmation';
    this.detectedCountry = this.dataset.detectedCountry || '';
    this.minimumAge = Number(this.dataset.minimumAge || 18);
    this.rememberDays = Number(this.dataset.rememberDays || 30);
    this.form = this.querySelector('form');
    this.error = this.querySelector('[data-sc-age-error]');
    this.dateInput = this.querySelector('[data-sc-age-date]');
    this.calendarInput = this.querySelector('[data-sc-age-calendar]');
    this.denyButtons = Array.from(this.querySelectorAll('[data-sc-age-deny]'));
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleDeny = this.handleDeny.bind(this);
    this.handleDateInput = this.handleDateInput.bind(this);
    this.handleDateFocus = this.handleDateFocus.bind(this);
    this.handleCalendarChange = this.handleCalendarChange.bind(this);

    if (this.isVerified()) {
      this.remove();
      return;
    }

    document.documentElement.classList.add('sc-age-gate-active');
    document.documentElement.classList.add('sc-modal-open');
    this.form?.addEventListener('submit', this.handleSubmit);
    this.dateInput?.addEventListener('input', this.handleDateInput);
    this.dateInput?.addEventListener('focus', this.handleDateFocus);
    this.calendarInput?.addEventListener('change', this.handleCalendarChange);
    this.denyButtons.forEach((button) => button.addEventListener('click', this.handleDeny));
    window.requestAnimationFrame(() => {
      this.querySelector('input, button, [tabindex]:not([tabindex="-1"])')?.focus();
    });
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.dateInput?.removeEventListener('input', this.handleDateInput);
    this.dateInput?.removeEventListener('focus', this.handleDateFocus);
    this.calendarInput?.removeEventListener('change', this.handleCalendarChange);
    this.denyButtons?.forEach((button) => button.removeEventListener('click', this.handleDeny));
    document.documentElement.classList.remove('sc-modal-open');
  }

  isVerified() {
    try {
      const stored = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
      return stored?.verified === true && stored.expiresAt && Date.now() < stored.expiresAt;
    } catch (_error) {
      return false;
    }
  }

  handleSubmit(event) {
    if (this.verificationMethod === 'date_of_birth') {
      const formData = new FormData(this.form);
      const dateOfBirth = this.normaliseDate(this.dateInput?.value || '');
      if (!dateOfBirth || !this.isOldEnough(dateOfBirth)) {
        event.preventDefault();
        this.dateInput?.setAttribute('aria-invalid', 'true');
        this.showError(this.dateInput);
        return;
      }

      const selectedCountry = String(formData.get('country_code') || '');
      this.rememberVerification(selectedCountry);
      if (selectedCountry && selectedCountry !== this.detectedCountry) return;

      event.preventDefault();
      this.unlock();
      return;
    }

    event.preventDefault();
    this.verify();
  }

  handleDeny(event) {
    event.preventDefault();
    this.showError();
  }

  handleDateInput(event) {
    const input = event.currentTarget;
    input.value = this.formatDate(input.value);
    const normalisedDate = this.normaliseDate(input.value);
    if (normalisedDate && this.calendarInput) this.calendarInput.value = normalisedDate;
    input.removeAttribute('aria-invalid');
    this.error?.setAttribute('hidden', '');
  }

  handleDateFocus(event) {
    const input = event.currentTarget;
    if (input.value === input.dataset.defaultDate) input.select();
  }

  handleCalendarChange(event) {
    const selectedDate = this.formatCalendarDate(event.currentTarget.value);
    if (!selectedDate || !this.dateInput) return;

    this.dateInput.value = selectedDate;
    this.dateInput.removeAttribute('aria-invalid');
    this.error?.setAttribute('hidden', '');
  }

  formatCalendarDate(value) {
    const parts = String(value || '').split('-');
    if (parts.length !== 3) return '';
    const [year, month, day] = parts;
    if (!/^\d{4}$/.test(year) || !/^\d{2}$/.test(month) || !/^\d{2}$/.test(day)) return '';
    return `${day} / ${month} / ${year}`;
  }

  formatDate(value) {
    const rawValue = String(value || '');
    const separatedParts = rawValue.match(/\d+/g) || [];
    const isPartialSeparatedDate = separatedParts.length === 1 && separatedParts[0].length <= 2
      || separatedParts.length === 2 && separatedParts[0].length <= 2 && separatedParts[1].length <= 2
      || separatedParts.length === 3 && separatedParts[0].length <= 2 && separatedParts[1].length <= 2 && separatedParts[2].length <= 4;
    if (/\D/.test(rawValue) && isPartialSeparatedDate) {
      const [day = '', month = '', year = ''] = separatedParts;
      let formatted = [day, month, year].filter(Boolean).join(' / ');
      if (/\D\s*$/.test(rawValue) && separatedParts.length < 3) formatted += ' / ';
      if (separatedParts.length === 3 && year.length === 4) {
        formatted = `${day.padStart(2, '0')} / ${month.padStart(2, '0')} / ${year}`;
      }
      return formatted;
    }
    const digits = rawValue.replace(/\D/g, '').slice(0, 8);
    const day = digits.slice(0, 2);
    const month = digits.slice(2, 4);
    const year = digits.slice(4, 8);
    return [day, month, year].filter(Boolean).join(' / ');
  }

  normaliseDate(value) {
    const rawValue = String(value || '');
    const separatedParts = rawValue.match(/\d+/g) || [];
    if (separatedParts.length === 3) {
      const [day, month, year] = separatedParts;
      if (day.length <= 2 && month.length <= 2 && year.length === 4) {
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
    const digits = rawValue.replace(/\D/g, '');
    if (digits.length !== 8) return '';
    const day = digits.slice(0, 2);
    const month = digits.slice(2, 4);
    const year = digits.slice(4, 8);
    return `${year}-${month}-${day}`;
  }

  verify() {
    this.rememberVerification();
    this.unlock();
  }

  rememberVerification(countryCode = this.detectedCountry) {
    const expiresAt = Date.now() + this.rememberDays * 24 * 60 * 60 * 1000;
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({ verified: true, expiresAt, countryCode }));
    } catch (_error) {
      // Verification should still unlock the page when storage is unavailable.
    }
  }

  unlock() {
    document.documentElement.classList.remove('sc-age-gate-active');
    document.documentElement.classList.remove('sc-modal-open');
    this.remove();
  }

  showError(focusTarget) {
    this.error?.removeAttribute('hidden');
    focusTarget?.focus?.();
    if (!focusTarget) this.error?.focus?.();
  }

  isOldEnough(value) {
    const parts = value.split('-').map(Number);
    if (parts.length !== 3 || parts.some((part) => !Number.isInteger(part))) return false;
    const [year, month, day] = parts;
    const dob = new Date(year, month - 1, day);
    if (dob.getFullYear() !== year || dob.getMonth() !== month - 1 || dob.getDate() !== day) return false;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age -= 1;
    return age >= this.minimumAge;
  }
}

if (!customElements.get('sc-age-gate')) {
  customElements.define('sc-age-gate', ScAgeGate);
}

class ScContentHub extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('[data-sc-hub-filter]'));
    this.items = Array.from(this.querySelectorAll('[data-sc-hub-item]'));
    this.filterHandlers = this.buttons.map((button) => {
      const handler = () => this.filter(button.dataset.scHubFilter || 'all');
      button.addEventListener('click', handler);
      return { button, handler };
    });
  }

  disconnectedCallback() {
    this.filterHandlers?.forEach(({ button, handler }) => button.removeEventListener('click', handler));
  }

  filter(value) {
    this.buttons.forEach((button) => {
      const isActive = (button.dataset.scHubFilter || 'all') === value;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    this.items.forEach((item) => {
      const shouldShow = value === 'all' || item.dataset.scHubCategory === value;
      item.toggleAttribute('hidden', !shouldShow);
    });
  }
}

if (!customElements.get('sc-content-hub')) {
  customElements.define('sc-content-hub', ScContentHub);
}

class ScAnchorNav extends HTMLElement {
  connectedCallback() {
    this.links = Array.from(this.querySelectorAll('[data-sc-anchor-link]'));
    this.targets = this.links
      .map((link) => document.getElementById(link.dataset.scAnchorLink))
      .filter(Boolean);

    if (!this.links.length || !this.targets.length || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver((entries) => this.updateActive(entries), {
      rootMargin: '-35% 0px -55% 0px',
      threshold: 0,
    });

    this.targets.forEach((target) => this.observer.observe(target));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  updateActive(entries) {
    const visible = entries.find((entry) => entry.isIntersecting);
    if (!visible) return;
    const activeId = visible.target.id;
    this.links.forEach((link) => {
      const isActive = link.dataset.scAnchorLink === activeId;
      link.classList.toggle('is-active', isActive);
      if (isActive) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('sc-anchor-nav')) {
  customElements.define('sc-anchor-nav', ScAnchorNav);
}

class ScVideoHero extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-sc-video-toggle]');
    this.videos = Array.from(this.querySelectorAll('video'));
    this.handleToggle = () => this.toggleVideos();
    this.handleVisibility = () => this.syncPlayback();
    this.handleViewport = () => this.syncPlayback();
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.toggle?.addEventListener('click', this.handleToggle);
    document.addEventListener('visibilitychange', this.handleVisibility);
    this.motionQuery.addEventListener?.('change', this.handleVisibility);
    this.mobileQuery.addEventListener?.('change', this.handleViewport);
    this.syncPlayback();
    this.setupContentAutoHide();
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.handleToggle);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    this.motionQuery?.removeEventListener?.('change', this.handleVisibility);
    this.mobileQuery?.removeEventListener?.('change', this.handleViewport);
    this.teardownContentAutoHide();
  }

  setupContentAutoHide() {
    this.content = this.querySelector('.sc-video-hero__content');
    this.contentAutoHideEnabled = this.dataset.contentAutoHide === 'true' && !window.Shopify?.designMode;
    if (!this.contentAutoHideEnabled || !this.content) return;

    this.contentHideDelay = Math.max(2, Number(this.dataset.contentHideDelay || 6)) * 1000;
    this.contentInView = false;
    this.handleContentActivity = () => {
      if (this.contentInView) this.showContent(true);
    };
    this.handleContentFocusOut = () => {
      window.requestAnimationFrame(() => {
        if (!this.contains(document.activeElement) && this.contentInView) this.scheduleContentHide();
      });
    };

    this.addEventListener('pointermove', this.handleContentActivity, { passive: true });
    this.addEventListener('pointerdown', this.handleContentActivity, { passive: true });
    this.addEventListener('focusin', this.handleContentActivity);
    this.addEventListener('focusout', this.handleContentFocusOut);

    if ('IntersectionObserver' in window) {
      this.contentObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        const isVisible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.2);
        this.contentInView = isVisible;
        if (isVisible) this.showContent(true);
        else this.showContent(false);
      }, { threshold: [0, 0.2, 0.5] });
      this.contentObserver.observe(this);
    } else {
      this.contentInView = true;
      this.scheduleContentHide();
    }
  }

  teardownContentAutoHide() {
    window.clearTimeout(this.contentHideTimer);
    this.contentObserver?.disconnect();
    this.removeEventListener('pointermove', this.handleContentActivity);
    this.removeEventListener('pointerdown', this.handleContentActivity);
    this.removeEventListener('focusin', this.handleContentActivity);
    this.removeEventListener('focusout', this.handleContentFocusOut);
  }

  scheduleContentHide() {
    window.clearTimeout(this.contentHideTimer);
    if (!this.contentInView || !this.content) return;
    this.contentHideTimer = window.setTimeout(() => this.hideContent(), this.contentHideDelay);
  }

  hideContent() {
    if (!this.content || this.content.contains(document.activeElement)) {
      this.scheduleContentHide();
      return;
    }
    this.classList.add('is-content-hiding');
    this.content.setAttribute('aria-hidden', 'true');
    this.content.inert = true;
  }

  showContent(reschedule = true) {
    window.clearTimeout(this.contentHideTimer);
    if (!this.content) return;
    this.classList.remove('is-content-hiding');
    this.content.removeAttribute('aria-hidden');
    this.content.inert = false;
    if (reschedule) this.scheduleContentHide();
  }

  toggleVideos() {
    if (!this.videos.length) return;
    const activeVideo = this.getActiveVideo();
    if (!activeVideo) return;
    const shouldPlay = activeVideo.paused;
    this.videos.forEach((video) => {
      if (video === activeVideo && shouldPlay) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
    this.updateToggle(shouldPlay);
  }

  getActiveVideo() {
    const mobileVideo = this.querySelector('.sc-video-hero__video--mobile');
    const desktopVideo = this.querySelector('.sc-video-hero__video--desktop');
    if (this.mobileQuery?.matches && mobileVideo) return mobileVideo;
    return desktopVideo || mobileVideo || null;
  }

  syncPlayback() {
    const activeVideo = this.getActiveVideo();
    const shouldPlay = this.dataset.autoplay === 'true' && !this.motionQuery?.matches && !document.hidden;
    this.videos.forEach((video) => {
      if (video === activeVideo && shouldPlay) video.play().catch(() => {});
      else video.pause();
    });
    this.updateToggle(Boolean(activeVideo && !activeVideo.paused));
  }

  updateToggle(isPlaying) {
    if (!this.toggle) return;
    this.toggle.textContent = isPlaying ? 'Pause' : 'Play';
    this.toggle.setAttribute('aria-label', isPlaying ? 'Pause background video' : 'Play background video');
  }
}

if (!customElements.get('sc-video-hero')) {
  customElements.define('sc-video-hero', ScVideoHero);
}

class ScGiftMessage extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('[data-sc-gift-toggle]');
    this.field = this.querySelector('[data-sc-gift-field]');
    this.count = this.querySelector('[data-sc-gift-count]');
    this.checkbox?.addEventListener('change', () => this.updateVisibility());
    this.field?.addEventListener('input', () => this.updateCount());
    this.updateVisibility();
    this.updateCount();
  }

  updateVisibility() {
    if (!this.checkbox || !this.field) return;
    const wrapper = this.querySelector('[data-sc-gift-wrapper]');
    wrapper?.toggleAttribute('hidden', !this.checkbox.checked);
    this.field.disabled = !this.checkbox.checked;
  }

  updateCount() {
    if (!this.field || !this.count) return;
    this.count.textContent = String(this.field.value.length);
  }
}

if (!customElements.get('sc-gift-message')) {
  customElements.define('sc-gift-message', ScGiftMessage);
}

if (!window.__scRevealInit) {
  window.__scRevealInit = true;
  const reveal = () => {
    const items = document.querySelectorAll('.sc-reveal:not(.is-visible)');
    if (!('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    items.forEach((item) => observer.observe(item));
  };
  document.addEventListener('DOMContentLoaded', reveal);
  document.addEventListener('shopify:section:load', reveal);
}
